// Axios interceptor for session token management and single login enforcement
import axios from 'axios';

/**
 * Setup axios interceptors for session token management
 */
export const setupAxiosInterceptors = (navigate) => {
  // Request interceptor: Add session token to all requests
  axios.interceptors.request.use(
    (config) => {
      // Never attach session headers to external APIs (e.g. api.github.com).
      // Those servers don't allow our custom headers in CORS preflight.
      const url = config.url || '';
      const isExternal = url.startsWith('http') &&
        !url.includes('theskillgenie.com') &&
        !url.includes('localhost');
      if (isExternal) {
        // Also strip any session headers that were pre-set as axios defaults
        delete config.headers.Authorization;
        delete config.headers['X-Session-Token'];
        return config;
      }

      // Get session token from localStorage
      const sessionToken = localStorage.getItem('session_token');

      if (sessionToken) {
        // Add session token to Authorization header
        config.headers.Authorization = `Bearer ${sessionToken}`;
        // Also add as X-Session-Token header for compatibility
        config.headers['X-Session-Token'] = sessionToken;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle session invalidation
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const { response } = error;
      
      // Check if session is invalid (401 with SESSION_INVALID code)
      if (response?.status === 401) {
        const errorData = response.data;
        const errorCode = errorData?.code;
        
        // Handle session invalidation (another device logged in)
        if (errorCode === 'SESSION_INVALID' || errorCode === 'SESSION_REQUIRED') {
          // Clear all session data
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_pk');
          localStorage.removeItem('auth_token');
          
          // Clear axios default headers
          delete axios.defaults.headers.common.Authorization;
          delete axios.defaults.headers.common['X-Session-Token'];
          
          // Show message to user
          const message = errorData?.error || 
            'Your session has been invalidated. Another device may have logged in. Please log in again.';
          
          // Use window.location for navigation if navigate is not available
          if (navigate) {
            navigate('/login', { 
              state: { 
                error: message,
                sessionInvalidated: true 
              } 
            });
          } else {
            // Fallback: use window.location
            alert(message);
            window.location.href = '/login';
          }
        }
      }
      
      return Promise.reject(error);
    }
  );
};

/**
 * Initialize axios interceptors on app startup
 * Call this in your App.jsx or main entry point
 */
export const initAxiosInterceptors = () => {
  // Get session token and set default headers if it exists
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${sessionToken}`;
    axios.defaults.headers.common['X-Session-Token'] = sessionToken;
  }
};

/**
 * Clear session and logout user
 * Call this when user explicitly logs out
 */
export const clearSession = async () => {
  try {
    const sessionToken = localStorage.getItem('session_token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isCollege = user.role === 'college';
    const logoutUrl = isCollege 
      ? `${process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com'}/colleges/logout`
      : `${process.env.REACT_APP_API_URL || 'https://api.theskillgenie.com'}/user/logout`;
    
    if (sessionToken) {
      // Call logout API to clear session on server
      await axios.post(logoutUrl, {}, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'X-Session-Token': sessionToken
        }
      });
    }
  } catch (error) {
    console.error('Logout API error (continuing with local cleanup):', error);
  } finally {
    // Clear all session data locally regardless of API call result
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_pk');
    localStorage.removeItem('auth_token');
    
    // Clear axios default headers
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common['X-Session-Token'];
  }
};




